import { useObserver } from "mobx-react"
import { useRouter } from "next/router"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useLocalStorage } from "react-use"
import styled, { css } from "styled-components"

import { useSWRForFetchGetStudentTodoStatus } from "~/api/student/todo-status/get"
import Badge from "~/components/atoms/Badge"
import CtaLink from "~/components/atoms/CtaLink"
import FunctionLink from "~/components/atoms/FunctionLink"
import Icon from "~/components/atoms/Icon"
import Logo, { LogoType, LogoURL } from "~/components/atoms/Logo"
import Pc from "~/components/atoms/Pc"
import Sp from "~/components/atoms/Sp"
import Typography from "~/components/atoms/Typography"
import { Container } from "~/components/layouts/Container"
import ActionPairButton from "~/components/molecules/ActionPairButton"
import BalloonMenu from "~/components/molecules/BalloonMenu"
import Tab from "~/components/molecules/Tab"
import { Color, LineHeight, SpacingSize, TextSize, Transition, Z } from "~/consts/style-mixins"
import { internalLink } from "~/services/helper"
import { useStores } from "~/stores/StoreProvider"

import { Campaign2021Modal } from "./_Campaign2021Modal"
import { FlowMessageForCampaign } from "./_FlowMessageForCampaign"
import { FlowMessageForConfirmGraduatesIn } from "./_FlowMessageForConfirmGraduatesIn"
import { FlowMessageForPromptToEnterProfile } from "./_FlowMessageForPromptToEnterProfile"

interface Props {
  showMenu?: boolean
  showTab?: boolean
  showFlowLink?: boolean
  selectedTab?: keyof typeof TabType
  logoType: keyof typeof LogoType
}

interface StyledProps {
  showDrawer: boolean
}

const TabType = {
  LongInternship: 1,
  Event: 2,
}

const TRACKING_PARAM = "?access_from=header-nav"

const Header = ({ selectedTab, showTab, logoType, showMenu = true, showFlowLink = true }: Props) => {
  const { userStore, profileStore, inputProfileFlowStore } = useStores()
  const { data: todoStatusJson } = useSWRForFetchGetStudentTodoStatus()
  const router = useRouter()

  const [showPopup, userClick] = useState(false)
  const [showDrawer, hamburgerClick] = useState(false)
  const [isValidCampaign, setIsValidCampaign] = useState(false)

  const [isValidModalForCampaign, setIsValidModalForCampaign] = useLocalStorage("infra-campaign-2021-modal", true)

  const hrefForRegister = useMemo(() => {
    return internalLink(`/auth/register?redirect_after_auth=${router.pathname}`)
  }, [router])

  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    profileStore.fetchProfileDetail().then((result) => {
      setIsLoaded(true)
      if (profileStore.isValidCampaign) {
        setIsValidCampaign(true)
      }
      if (result?.json) {
        inputProfileFlowStore.fetchJudgeEntered()
      }
    })
  }, [])

  const flowMessageMode = useMemo(() => {
    if (!isLoaded || !todoStatusJson?.data) {
      return "hide"
    }
    const { show_confirm_year, show_input_flow } = todoStatusJson.data

    if (show_confirm_year && !router.asPath.includes("profile")) {
      return "confirm_graduates_in"
    }

    if (profileStore.isValidCampaign) {
      return "campaign2021"
    }

    const isShowPromptToEnter = show_input_flow //inputProfileFlowStore.totalPageCount - inputProfileFlowStore.enteredPageCount > 0
    // キャンペーン応募済みのユーザーには表示しない ↓ 常にfalse
    if (isShowPromptToEnter /*&& !profileStore.isAppliedToCampaign*/) {
      return "prompt_to_enter"
    }

    return "hide"
  }, [isLoaded, profileStore.isValidCampaign, todoStatusJson, router.asPath])

  const onCloseCampaignModal = useCallback((result?: "show-detail") => {
    setIsValidModalForCampaign(false)
    if (result === "show-detail") {
      router.push(internalLink("/new-grads-profile-campaign-2021?access_from=announcement-modal"))
    }
  }, [])

  return (
    <>
      <StyledHeaderWrapper>
        <Container size="Large">
          <StyledHeaderInner>
            <StyledLogo href={LogoURL[logoType]}>
              <Logo type={logoType} height={32} />
            </StyledLogo>
            {showMenu && (
              <>
                <Pc>
                  {useObserver(() =>
                    userStore.authed && userStore.user ? (
                      userStore.authedAsStudent ? (
                        <StyledRightElemWrapper>
                          <StyledEachElem></StyledEachElem>
                          <StyledEachElem>
                            <StyledLink href={`${process.env.INFRA_INTERN_URL}/my-page/favorites`}>
                              お気に入り
                            </StyledLink>
                          </StyledEachElem>
                          <StyledEachElem>
                            <StyledLink href={`${process.env.INFRA_INTERN_URL}/my-page`}>応募管理</StyledLink>
                          </StyledEachElem>
                          <StyledEachElem>
                            <StyledLink href={`${process.env.INFRA_INTERN_URL}/my-page/scouts`}>
                              スカウト管理
                            </StyledLink>
                          </StyledEachElem>
                          <StyledEachElem>
                            <StyledLink href={internalLink("/badges")}>実績バッジ</StyledLink>
                          </StyledEachElem>
                          <StyledEachElem>
                            <StyledLink href={internalLink("/messages")}>メッセージ</StyledLink>
                            {userStore.user.unreadMessageCount !== undefined &&
                              userStore.user.unreadMessageCount > 0 && (
                                <Badge count={userStore.user.unreadMessageCount} className="badge" />
                              )}
                          </StyledEachElem>
                          <StyledEachElem
                            onClick={() => {
                              userClick(!showPopup)
                            }}
                          >
                            <StyledUserMenu>
                              <StyledProfileImage src={userStore.user.profileImg} alt="プロフィール画像" />
                              <Icon size={10} type={"Open"} color={Color.Gray} />
                            </StyledUserMenu>
                            {showPopup && (
                              <StyledBalloonMenu className="user">
                                <BalloonMenu
                                  items={[
                                    {
                                      name: "プロフィール編集",
                                      href: internalLink("/profile"),
                                    },
                                    {
                                      name: "アカウント設定",
                                      href: `${process.env.INFRA_INTERN_URL}/my-page/account-setting`,
                                    },
                                    {
                                      name: "ログアウト",
                                      href: `${process.env.INFRA_INTERN_URL}/auth/logout`,
                                    },
                                  ]}
                                />
                              </StyledBalloonMenu>
                            )}
                          </StyledEachElem>
                        </StyledRightElemWrapper>
                      ) : (
                        <StyledRightElemWrapper>
                          <StyledEachElem>
                            <StyledLink href={`${process.env.INFRA_ENTERPRISE_URL}/`}>管理画面</StyledLink>
                          </StyledEachElem>
                          <StyledEachElem
                            onClick={() => {
                              userClick(!showPopup)
                            }}
                          >
                            <StyledUserMenu>
                              <StyledLink href={`${process.env.INFRA_INTERN_URL}/my-page/`}>
                                {userStore.user.name}
                              </StyledLink>
                              <Icon size={10} type={"Open"} color={Color.Gray} />
                            </StyledUserMenu>
                            {showPopup && (
                              <StyledBalloonMenu>
                                <BalloonMenu
                                  items={[
                                    {
                                      name: "ログアウト",
                                      href: `${process.env.INFRA_INTERN_URL}/auth/logout`,
                                    },
                                  ]}
                                />
                              </StyledBalloonMenu>
                            )}
                          </StyledEachElem>
                        </StyledRightElemWrapper>
                      )
                    ) : (
                      <StyledRightElemWrapper>
                        <StyledEachElem>
                          <StyledLink href={`${process.env.INFRA_INTERN_URL}/pages/contact-for-corp`}>
                            採用担当者様はこちら
                          </StyledLink>
                        </StyledEachElem>
                        <StyledEachElem>
                          <StyledLink href={`${process.env.INFRA_INTERN_URL}/auth/login`}>ログイン</StyledLink>
                        </StyledEachElem>
                        <StyledEachElem>
                          <StyledCtaLink size="Small" href={hrefForRegister}>
                            新規登録
                          </StyledCtaLink>
                        </StyledEachElem>
                      </StyledRightElemWrapper>
                    )
                  )}
                </Pc>
                <Sp>
                  <StyledHamburger
                    onClick={() => {
                      hamburgerClick(!showDrawer)
                    }}
                  >
                    <Icon size={24} type={"Hamburger"} color={Color.Black} />
                  </StyledHamburger>
                </Sp>
              </>
            )}
          </StyledHeaderInner>
          {showTab && (
            <StyledTabWrapper>
              <StyledTab
                size="Medium"
                selectedTabIndex={selectedTab && TabType[selectedTab]}
                items={[
                  {
                    name: "長期インターン",
                    href: `${process.env.INFRA_INTERN_URL}`,
                  },
                  {
                    name: "イベント",
                    href: internalLink("/events") + TRACKING_PARAM,
                  },
                ]}
              />
            </StyledTabWrapper>
          )}
        </Container>
      </StyledHeaderWrapper>

      {/* キャンペーンLP と LPから遷移したプロフィール編集画面 ではキャンペーン告知は表示しない */}
      {router.pathname !== "/next/new-grads-profile-campaign-2021" &&
        router.query.access_from !== "new-grads-profile-campaign-2021-lp" && (
          <>
            {flowMessageMode === "campaign2021" && <FlowMessageForCampaign />}
            {isValidCampaign && isValidModalForCampaign && <Campaign2021Modal onClose={onCloseCampaignModal} />}
          </>
        )}

      {flowMessageMode === "confirm_graduates_in" && <FlowMessageForConfirmGraduatesIn />}
      {useObserver(
        () =>
          showFlowLink &&
          inputProfileFlowStore.isExistRoutingInfo &&
          flowMessageMode === "prompt_to_enter" && <FlowMessageForPromptToEnterProfile />
      )}

      {useObserver(
        () =>
          showDrawer && (
            <>
              <StyledOverlay
                showDrawer={showDrawer}
                onClick={() => {
                  hamburgerClick(!showDrawer)
                }}
              />
              <StyledDrawer showDrawer={showDrawer}>
                {userStore.authed && userStore.user ? (
                  userStore.authedAsCorporate ? (
                    <>
                      <StyledDrawerSection>
                        <StyledDrawerUser>
                          <Typography type="SubHeading">{userStore.user.name}</Typography>
                        </StyledDrawerUser>
                      </StyledDrawerSection>
                      <StyledDrawerSection>
                        <StyledDrawerItem href={`${process.env.INFRA_ENTERPRISE_URL}/`}>管理画面</StyledDrawerItem>
                      </StyledDrawerSection>
                      <StyledDrawerAccountSection>
                        <FunctionLink
                          onClick={() => window.open(`${process.env.INFRA_INTERN_URL}/auth/logout`)}
                          size="Medium"
                        >
                          ログアウト
                        </FunctionLink>
                      </StyledDrawerAccountSection>
                    </>
                  ) : (
                    <>
                      <StyledDrawerSection>
                        <StyledDrawerUser>
                          <StyledDrawerUserImage src={userStore.user.profileImg} alt="プロフィール画像" />
                          <Typography type="SubHeading">{userStore.user.name}</Typography>
                        </StyledDrawerUser>
                      </StyledDrawerSection>
                      <StyledDrawerSection>
                        <StyledDrawerItem href={`${process.env.INFRA_INTERN_URL}/my-page/favorites`}>
                          お気に入り
                        </StyledDrawerItem>
                        <StyledDrawerItem href={`${process.env.INFRA_INTERN_URL}/my-page`}>応募管理</StyledDrawerItem>
                        <StyledDrawerItem href={`${process.env.INFRA_INTERN_URL}/my-page/scouts`}>
                          スカウト管理
                        </StyledDrawerItem>
                        <StyledDrawerItem href={internalLink("/messages")}>メッセージ</StyledDrawerItem>
                        <StyledDrawerItem href={internalLink("/profile")}>プロフィール編集</StyledDrawerItem>
                        <StyledDrawerItem href={internalLink("/badges")}>実績バッジ</StyledDrawerItem>
                        <StyledDrawerItem href={`${process.env.INFRA_INTERN_URL}/my-page/account-setting`}>
                          アカウント設定
                        </StyledDrawerItem>
                      </StyledDrawerSection>
                      <StyledDrawerAccountSection>
                        <FunctionLink
                          onClick={() => window.open(`${process.env.INFRA_INTERN_URL}/auth/logout`)}
                          size="Medium"
                        >
                          ログアウト
                        </FunctionLink>
                      </StyledDrawerAccountSection>
                    </>
                  )
                ) : (
                  <>
                    <StyledDrawerSection>
                      <StyledDrawerCaption type="Note">採用担当者様向け</StyledDrawerCaption>
                      <StyledDrawerItem
                        href={`${process.env.INFRA_INTERN_URL}/pages/contact-for-corp`}
                        className="gtm_drawer-long-internship-corp-contact"
                      >
                        長期インターン採用について
                      </StyledDrawerItem>
                    </StyledDrawerSection>
                    <StyledDrawerAccountSection>
                      <ActionPairButton
                        vertical={true}
                        mainAction={{
                          label: "新規登録",
                          action: () =>
                            window.open(internalLink(`/auth/register?redirect_after_auth=${location.pathname}`)),
                        }}
                        subAction={{
                          label: "ログイン",
                          action: () => window.open(`${process.env.INFRA_INTERN_URL}/auth/login`),
                        }}
                      />
                    </StyledDrawerAccountSection>
                  </>
                )}
              </StyledDrawer>
            </>
          )
      )}
    </>
  )
}

const StyledLogo = styled.a`
  height: 32px;
`

const StyledHeaderWrapper = styled.div`
  background-color: ${Color.White};
  border-bottom: 1px solid ${Color.BorderGray};
  z-index: ${Z.Header};
`

const StyledHeaderInner = styled.div`
  padding: ${SpacingSize.M}px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`

const StyledRightElemWrapper = styled.div`
  display: flex;
  align-items: center;
`

const StyledEachElem = styled.span`
  position: relative;
  margin-right: ${SpacingSize.S}px;
  &:last-child {
    margin-right: 0;
  }
  .badge {
    position: absolute;
    top: -8px;
    right: -8px;
  }
`

const StyledLink = styled.a`
  font-size: ${TextSize.S}px;
  font-weight: bold;
  color: ${Color.Gray};
  text-decoration: none;
  ${Transition.Hover};
  &:hover {
    color: ${Color.Black};
  }
`

const StyledUserMenu = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

const StyledProfileImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #000;
  object-fit: cover;
  margin-right: ${SpacingSize["3XS"]}px;
`

const StyledBalloonMenu = styled.div`
  position: absolute;
  z-index: ${Z.Popup};
  min-width: 160px;
  width: max-content;
  &.user {
    right: -54px;
    top: 48px;
  }
  &.corp {
    right: -36px;
    top: 36px;
  }
`

const StyledCtaLink = styled(CtaLink)`
  width: 100px;
  margin: -4px 0;
`

const StyledTabWrapper = styled.div`
  overflow: scroll;
  -webkit-overflow-scrolling: touch;
  margin: -12px -12px 0 -12px;
  padding: 0 10px;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const StyledTab = styled(Tab)`
  border-bottom: none;
`

const StyledHamburger = styled.div`
  padding: 3px ${SpacingSize["3XS"]}px;
  cursor: pointer;
`

const StyledDrawer = styled.div`
  height: 100%;
  width: 240px;
  position: fixed;
  z-index: ${Z.Drawer};
  top: 0;
  right: 0;
  background: ${Color.White};
  overflow: scroll;
  -webkit-overflow-scrolling: touch;
  ${({ showDrawer }: StyledProps) => css`
    ${showDrawer &&
    css`
      animation-name: open;
      animation-duration: 0.4s;
      transition-timing-function: ease-out;
      @keyframes open {
        0% {
          transform: translate(240px);
        }
        100% {
          transform: translate(0);
        }
      }
    `}
    ${!showDrawer &&
    css`
      animation-name: close;
      animation-duration: 0.4s;
      transition-timing-function: ease-out;
      @keyframes close {
        0% {
          transform: translate(0);
        }
        100% {
          transform: translate(240px);
        }
      }
    `}
  `}
`

const StyledOverlay = styled.div`
  height: 100%;
  width: 100%;
  position: fixed;
  z-index: ${Z.Overlay};
  top: 0;
  left: 0;
  background: #000;
  opacity: 0.6;
  ${({ showDrawer }: StyledProps) => css`
    ${showDrawer &&
    css`
      animation-name: fadeIn;
      animation-duration: 0.4s;
      transition-timing-function: ease-out;
      @keyframes fadeIn {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 0.6;
        }
      }
    `}
    ${!showDrawer &&
    css`
      animation-name: fadeOut;
      animation-duration: 0.4s;
      transition-timing-function: ease-out;
      @keyframes fadeOut {
        0% {
          opacity: 0.6;
        }
        100% {
          opacity: 0;
        }
      }
    `}
  `}
`

const StyledDrawerSection = styled.div`
  padding: ${SpacingSize.M}px 0;
  border-bottom: solid 1px ${Color.BorderGray};
`

const StyledDrawerAccountSection = styled.div`
  padding: ${SpacingSize["2XL"]}px ${SpacingSize.M}px;
`

const StyledDrawerItem = styled.a`
  display: block;
  padding: ${SpacingSize.M}px;
  font-size: ${TextSize.M}px;
  font-weight: bold;
  color: ${Color.Gray};
  ${Transition.Hover};
  &:hover {
    color: ${Color.Black};
  }
`
const StyledDrawerCaption = styled(Typography)`
  padding: ${SpacingSize.L}px ${SpacingSize.M}px ${SpacingSize["3XS"]}px;
  color: ${Color.LightGray};
`

const StyledDrawerUser = styled.div`
  display: flex;
  align-items: center;
  padding: 0 ${SpacingSize.M}px;
  text-align: center;
`

const StyledDrawerUserImage = styled.img`
  height: 64px;
  width: 64px;
  border-radius: 50%;
  border: solid 1px ${Color.BorderGray};
  margin-right: ${SpacingSize.S}px;
`

export default Header
